import { isEqual } from 'lodash'

import { desktop } from '../store/defaultSettings'
import export_config from '../../../../lib/plottr_import_export/src/exporter/default_config'
import { validCustomTemplateOrNull, validBuiltInTemplateOrNull } from './plottrFileSchemaValidation'

/**
 * @typedef {import('../index').Note} Note
 * @typedef {import('../index').Character} Character
 * @typedef {import('../index').Place} Place
 * @typedef {import('../index').Tag} Tag
 * @typedef {import('../index').Book} Book
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').PlottrFileBeatsEntry} Beats
 * @typedef {import('../index').FamilyTree} FamilyTree
 * @typedef {import('../index').Series} Series
 * @typedef {import('./plottr').HierarchyLevels} HierarchyLevelsForOneFile
 */

/**
 * @template A
 * @typedef ActionableData
 * @property {A} data
 * @property {'INCLUDE'|'DUPLICATE'|'OVERWRITE'} action
 */

/**
 * @template A
 * @typedef LocalAndOnline
 * @property {A} onlineEntity
 * @property {A | undefined} offlineEntity
 */

/**
 * @typedef HasId
 * @property {number} id
 */

/**
 * @typedef BookWithAllAssociatedData
 * @property {Partial<Book> & HasId} book
 * @property {Array<Partial<Line> & HasId>} lines
 * @property {Array<Partial<Card> & HasId>} cards
 * @property {Partial<Beats>} beats
 * @property {Partial<HierarchyLevelsForOneFile>} hierarchy
 */

/**
 * @typedef ConflictChanges
 * @property {Array<ActionableData<Note>>} notes
 * @property {Array<ActionableData<Character>>} characters
 * @property {Array<ActionableData<Place>>} places
 * @property {Array<ActionableData<Tag>>} tags
 * @property {Array<ActionableData<BookWithAllAssociatedData>>} books
 * @property {Array<ActionableData<FamilyTree>>} familyTrees
 */

/**
 * @typedef LocalAndOnlineConflictChanges
 * @property {Array<ActionableData<LocalAndOnline<Note>>>} notes
 * @property {Array<ActionableData<LocalAndOnline<Character>>>} characters
 * @property {Array<ActionableData<LocalAndOnline<Place>>>} places
 * @property {Array<ActionableData<LocalAndOnline<Tag>>>} tags
 * @property {Array<ActionableData<LocalAndOnline<BookWithAllAssociatedData>>>} books
 * @property {Array<ActionableData<LocalAndOnline<FamilyTree>>>} familyTrees
 */

/**
 * @typedef SeriesConflictData
 * @property {Series} onlineSeries
 * @property {Series} offlineSeries
 */

/**
 * @typedef SeriesConflict
 * @property {'OVERWRITE' | 'DISCARD'} action
 * @property {SeriesConflictData} data
 */

/**
 * @typedef {import('plottr_locales').Locale} Locale
 */

/**
 * @typedef {import('./plottr').Slate} Slate
 * @typedef {import('./plottr').PlottrFileCard} PlottrFileCard
 * @typedef {import('./plottr').Line} Line
 * @typedef {import('./plottr').PlottrFileBeats} PlottrFileBeats
 * @typedef {import('./plottr').PlottrFileBeatsEntry} PlottrFileBeatsEntry
 * @typedef {import('./plottr').TagsPlottrFileEntry} TagsPlottrFileEntry
 * @typedef {import('./plottr').HierarchyLevelsPlottrFile} HierarchyLevelsPlottrFile
 * @typedef {import('./plottr').HierarchyLevels} HierarchyLevels
 * @typedef {import('./plottr').HierarchyLevel} HierarchyLevel
 * @typedef {import('./plottr').LegacyCustomAttributes} LegacyCustomAttributes
 * @typedef {import('./plottr').NotesPlottrFileEntry} NotesPlottrFileEntry
 * @typedef {import('./plottr').CategoriesPlottrFile} CategoriesPlottrFile
 * @typedef {import('./plottr').Category} Category
 * @typedef {import('./plottr').PlottrFileImages} PlottrFileImages
 * @typedef {import('./plottr').ImageRecord} ImageRecord
 * @typedef {import('./plottr').PlottrFile} PlottrFile
 * @typedef {import('./plottr').Beat} Beat
 * @typedef {import('./plottr').PlottrFileBooks} PlottrFileBooks
 * @typedef {import('./plottr').ThemeHierarchyColors} ThemeHierarchyColors
 * @typedef {import('./plottr').LegacyCustomAttribute} LegacyCustomAttribute
 * @typedef {import('./plottr-in-your-pocket').Idea} Idea
 * @typedef {import('./plottr-in-your-pocket').Thread} Thread
 * @typedef {import('./plottr-in-your-pocket').UniversalIdeaId} UniversalIdeaId
 */

/**
 * @typedef {import('../index').FileURL} FileURL
 */

/**
 * NOTE(ed): we can't/shouldn't reference PltrAction from the result
 * of combining an action and a previous state.  It's somewhat
 * circular and will lead to headaches.
 *
 * @typedef SystemError
 * @property {Null | String} error
 * @property {Null | String} storeKey
 * @property {Null | any} action
 */

/**
 * @typedef {'owner'|'collaborator'|'viewer'} FilePermission
 */

/**
 * @typedef SystemPermission
 * @property {FilePermission | Null} permission
 */

/**
 * @typedef {ConflictChanges} ConflictResolutionLocalOnly
 */

/**
 * @typedef {LocalAndOnlineConflictChanges} ConflictResolutionChangedLocalAndOnline
 */

/**
 * @typedef ConflictResolution
 * @property {PlottrFile} onlineFile
 * @property {ConflictResolutionLocalOnly} localOnly
 * @property {ConflictResolutionChangedLocalAndOnline} changedLocalAndOnline
 * @property {Record<String, Array<LegacyCustomAttribute>>} customAttributes
 * @property {SeriesConflict | Null} seriesConflict
 */

/**
 * @typedef SystemProject
 * @property {FilePermission | Null} permission
 * @property {String | Null} fileURL
 * @property {Array<String>} userNameSearchResults
 * @property {boolean} fileLoaded
 * @property {boolean} isLoading
 * @property {boolean} isOffline
 * @property {boolean} resuming
 * @property {boolean} checkingOfflineDrift
 * @property {boolean} overwritingCloudWithBackup
 * @property {boolean} showResumeMessageDialog
 * @property {boolean} backingUpOfflineFile
 * @property {boolean} unsavedChanges
 * @property {ConflictResolution | Null} conflictResolution
 * @property {boolean} bootedOfflineFileWhilstOnline
 * @property {boolean} projectNamingModalIsVisible
 * @property {ProjectTemplate | PlotlineTemplate | PlotlineCustomTemplate | Null} template
 * @property {String | Null} defaultName
 */

/**
 * @typedef {Record<String, String>} DataClientIds
 */

/**
 * @typedef Client
 * @property {String | Null} userId
 * @property {String | Null} clientId
 * @property {String | Null} emailAddress
 * @property {boolean | Null} hasOnboarded
 * @property {boolean | Null} isOnWeb
 * @property {boolean} isLoggedIn
 * @property {DataClientIds} dataClientIds
 * @property {'DASHBOARD' |  'APP' | Null} legacy__currentAppState
 */

/**
 * @typedef TrialInfo
 * @property {number} startsAt
 * @property {number} endsAt
 * @property {number} extensions
 */

/**
 * @typedef MachineInfo
 * @property {String} id
 * @property {String} os
 * @property {String} name
 * @property {String} localUserName
 */

/**
 * @typedef PlottrLicense
 * @property {String} secret
 * @property {MachineInfo} machineInfo
 * @property {String | Null} expiresAt
 * @property {String} dateChecked
 */

/**
 * @typedef ProLicense
 * @property {String} secret
 * @property {MachineInfo} machineInfo
 * @property {String | Null} expiresAt
 * @property {String} dateChecked
 */

/**
 * @typedef SystemLicense
 * @property {TrialInfo | Null} trialInfo
 * @property {PlottrLicense | Null} plottrLicense
 * @property {ProLicense | Null} proLicense
 * @property {ReturnType<typeof setTimeout> | Null} licenseCheckInterval
 * @property {boolean} couldNotContactLicenseServer
 * @property {boolean} isProduction
 */

/**
 * @typedef Subscription
 * @property {String} startsAt
 * @property {String | Null} endsAt
 * @property {String} lastChecked
 */

/**
 * @typedef Subscriptions
 * @property {Subscription | Null} proSubscription
 * @property {Subscription | Null} plottrSubscription
 */

/**
 * @typedef Account
 * @property {Subscriptions} subscriptions
 */

/**
 * @typedef KnownFilesEntry
 * @property {Array<String>} [pathToContainingFolder]
 * @property {String} fileURL
 * @property {String} fileName
 * @property {number | Null} lastOpened
 * @property {String} [path]
 * @property {'owner'|'collaborator'|'viewer'} [permission]
 * @property {String} [id]
 * @property {boolean} [isTempFile]
 * @property {boolean} [isCloudFile]
 * @property {boolean} [isOfflineBackup]
 */

/**
 * @typedef CustomTemplateAttributesEntry
 * @property {string} type
 * @property {string} name
 */

/**
 * @typedef BaseCustomTemplate
 * @property {String} id
 * @property {'plotlines'|'characters'|'custom-characters'|'custom-scenes'} type
 * @property {String} version
 * @property {String} [name]
 * @property {String} description
 * @property {String} [link]
 * @property {boolean} [isCloudTemplate]
 */

/**
 * @typedef {'top' | 'middle' | 'bottom'} MergeBias
 */

/**
 * @typedef PlotlineCustomTemplateData
 * @property {Array<PlottrFileCard>} [cards]
 * @property {PlottrFileBeats} beats
 * @property {Array<Line>} lines
 * @property {HierarchyLevels} hierarchyLevels
 */

/**
 * @typedef PlotlineCustomTemplateExt
 * @property {'plotlines'} type
 * @property {PlotlineCustomTemplateData} templateData
 * @property {MergeBias} [mergeBias]
 * @property {String} [templateDataVersion]
 */

/**
 * @typedef CharacterCustomTemplateAttribute
 * @property {String} name
 * @property {'text'|'paragraph'} type
 * @property {String} description
 */

/**
 * @typedef CharacterCustomTemplateExt
 * @property {'custom-characters'} type
 * @property {Array<CharacterCustomTemplateAttribute>} attributes
 */

/**
 * @typedef LegacyCharacterCustomTemplateExt
 * @property {'characters'} type
 * @property {Array<CharacterCustomTemplateAttribute>} attributes
 */

/**
 * @typedef SceneCustomTemplateAttribute
 * @property {String} name
 * @property {'text'|'paragraph'} type
 * @property {String} description
 */

/**
 * @typedef SceneCustomTemplateExt
 * @property {'custom-scenes'} type
 * @property {Array<SceneCustomTemplateAttribute>} attributes
 */

/**
 * @typedef {PlotlineCustomTemplateExt & BaseCustomTemplate} PlotlineCustomTemplate
 */

/**
 * @typedef {LegacyCharacterCustomTemplateExt & BaseCustomTemplate} LegacyCharacterCustomTemplate
 */

/**
 * @typedef {CharacterCustomTemplateExt & BaseCustomTemplate} CharacterCustomTemplate
 */

/**
 * @typedef {SceneCustomTemplateExt & BaseCustomTemplate} SceneCustomTemplate
 */

/**
 * @typedef {PlotlineCustomTemplate | LegacyCharacterCustomTemplate | CharacterCustomTemplate | SceneCustomTemplate} CustomTemplate
 */

/**
 * @typedef {ProjectTemplate | CharacterTemplate | PlotlineTemplate | SceneTemplate} BuiltInTemplate
 */

/**
 * @typedef {Record<String, BuiltInTemplate>} BuiltInTemplateState
 */

/**
 * @typedef CharacterTemplateAttribute
 * @property {String} name
 * @property {'text'|'paragraph'} type
 * @property {String} description
 * @property {String} [link]
 */

/**
 * @typedef CharacterTemplate
 * @property {String} id
 * @property {'characters'} type
 * @property {String} name
 * @property {String} description
 * @property {String} version
 * @property {Array<CharacterTemplateAttribute>} attributes
 * @property {String} [link]
 */

/**
 * @typedef SceneTemplateAttribute
 * @property {String} name
 * @property {'text'|'paragraph'} type
 * @property {String} description
 * @property {String} [link]
 */

/**
 * @typedef SceneTemplate
 * @property {String} id
 * @property {'scenes'} type
 * @property {String} version
 * @property {String} name
 * @property {String} description
 * @property {String} [link]
 * @property {Array<SceneTemplateAttribute>} attributes
 */

/**
 * @typedef PlotlineTemplateData
 * @property {Array<PlottrFileCard>} cards
 * @property {PlottrFileBeats} beats
 * @property {Array<Line>} lines
 * @property {HierarchyLevels} hierarchyLevels
 */

/**
 * @typedef {'children' | 'comedy' | 'general' | 'horror' | 'romance' | 'mystery' | 'action' | 'screenplay' | 'shortstory' | 'playwriting' | 'poetry' | 'foundational' | 'screenwriting'} TemplateCategory
 */

/**
 * @typedef PlotlineTemplate
 * @property {String} id
 * @property {true} builtIn
 * @property {'plotlines'} type
 * @property {String} name
 * @property {String} description
 * @property {String} version
 * @property {Array<TemplateCategory>} categories
 * @property {PlotlineTemplateData} templateData
 * @property {String} initialVersion
 * @property {Array<String>} appliedMigrations
 * @property {String} [link]
 * @property {MergeBias} [mergeBias]
 * @property {String} templateDataVersion
 */

/**
 * @typedef ProjectTemplateData
 * @property {Array<PlottrFileCard>} cards
 * @property {PlottrFileBeats} beats
 * @property {Array<Line>} lines
 * @property {HierarchyLevelsPlottrFile} hierarchyLevels
 * @property {LegacyCustomAttributes} customAttributes
 * @property {Array<TagsPlottrFileEntry>} tags
 * @property {Array<NotesPlottrFileEntry>} notes
 * @property {CategoriesPlottrFile} categories
 * @property {PlottrFileImages} images
 * @property {PlottrFileBooks} [books]
 */

/**
 * @typedef ProjectTemplate
 * @property {String} id
 * @property {'project'} type
 * @property {String} name
 * @property {String} description
 * @property {String} version
 * @property {String} [link]
 * @property {ProjectTemplateData} templateData
 * @property {String} initialVersion
 * @property {Array<String>} appliedMigrations
 * @property {String} templateDataVersion
 */

/**
 * @typedef Templates
 * @property {BuiltInTemplateState} templates
 * @property {Array<CustomTemplate>} customTemplates
 */

/**
 * TODO: check this type!
 * @typedef {Object} GeneralScrivenerExportSettings
 */

/**
 * @typedef OutlineScrivenerExportSettings
 * @property {boolean} export
 * @property {'column' | 'row'} columnOrRow
 * @property {boolean} sceneCards
 * @property {boolean} plotlineInTitle
 * @property {boolean} attachments
 * @property {boolean} description
 * @property {boolean} descriptionHeading
 * @property {'synopsis' | 'body' | 'notes'} where
 * @property {boolean} customAttributes
 * @property {boolean} templates
 * @property {Array<number> | Null} filter
 */

/**
 * @typedef CharactersScrivenerExportSettings
 * @property {boolean} export
 * @property {boolean} heading
 * @property {boolean} description
 * @property {boolean} descriptionHeading
 * @property {boolean} attachments
 * @property {boolean} notesHeading
 * @property {boolean} notes
 * @property {boolean} customAttributes
 * @property {boolean} templates
 * @property {boolean} tags
 * @property {boolean} categoryHeading
 * @property {boolean} category
 */

/**
 * @typedef PlacesScrivenerExportSettings
 * @property {boolean} export
 * @property {boolean} heading
 * @property {boolean} description
 * @property {boolean} descriptionHeading
 * @property {boolean} attachments
 * @property {boolean} notesHeading
 * @property {boolean} notes
 * @property {boolean} customAttributes
 * @property {boolean} tags
 */

/**
 * @typedef NotesScrivenerExportSettings
 * @property {boolean} export
 * @property {boolean} heading
 * @property {boolean} attachments
 * @property {boolean} content
 */

/**
 * @typedef ScrivenerExportSettings
 * @property {GeneralScrivenerExportSettings} general
 * @property {OutlineScrivenerExportSettings} outline
 * @property {CharactersScrivenerExportSettings} characters
 * @property {PlacesScrivenerExportSettings} places
 * @property {NotesScrivenerExportSettings} notes
 */

/**
 * @typedef GeneralWordExportSettings
 * @property {boolean} titlePage
 */

/**
 * @typedef OutlineWordExportSettings
 * @property {boolean} export
 * @property {'column' | 'row'} columnOrRow
 * @property {boolean} heading
 * @property {boolean} sceneCards
 * @property {boolean} plotlineInTitle
 * @property {boolean} attachments
 * @property {boolean} description
 * @property {boolean} customAttributes
 * @property {boolean} templates
 * @property {Array<number> | Null} filter
 */

/**
 * @typedef CharactersWordExportSettings
 * @property {boolean} export
 * @property {boolean} heading
 * @property {boolean} images
 * @property {boolean} descriptionHeading
 * @property {boolean} description
 * @property {boolean} attachments
 * @property {boolean} notesHeading
 * @property {boolean} notes
 * @property {boolean} customAttributes
 * @property {boolean} templates
 * @property {boolean} categoryHeading
 * @property {boolean} category
 */

/**
 * @typedef PlacesWordExportSettings
 * @property {boolean} export
 * @property {boolean} heading
 * @property {boolean} images
 * @property {boolean} descriptionHeading
 * @property {boolean} description
 * @property {boolean} attachments
 * @property {boolean} notesHeading
 * @property {boolean} notes
 * @property {boolean} customAttributes
 * @property {boolean} category
 */

/**
 * @typedef NotesWordExportSettings
 * @property {boolean} export
 * @property {boolean} heading
 * @property {boolean} images
 * @property {boolean} attachments
 * @property {boolean} content
 * @property {boolean} customAttributes
 * @property {boolean} category
 */

/**
 * @typedef WordExportSettings
 * @property {GeneralWordExportSettings} general
 * @property {OutlineWordExportSettings} outline
 * @property {CharactersWordExportSettings} characters
 * @property {PlacesWordExportSettings} places
 * @property {NotesWordExportSettings} notes
 */

/**
 * @typedef ExportSettings
 * @property {boolean} saveSettings
 * @property {'word' | 'scrivener'} savedType
 * @property {ScrivenerExportSettings} scrivener
 * @property {WordExportSettings} word
 */

/**
 * @typedef RceFontsUserAppSettings
 * @property {String} defaultFont
 * @property {String} defaultFontSize
 * @property {String} defaultDarkModeFontColor
 * @property {String} defaultFontColor
 * @property {String} titleFont
 * @property {String} titleFontSize
 * @property {number} titleFontWeight
 * @property {String} titleDarkModeFontColor
 * @property {String} titleFontColor
 * @property {String} subtitleFont
 * @property {String} subtitleFontSize
 * @property {number} subtitleFontWeight
 * @property {String} subtitleDarkModeFontColor
 * @property {String} subtitleFontColor
 */

/**
 * @typedef GlobalFontsUserAppSettings
 * @property {String} headingFont
 * @property {String} bodyFont
 */

/**
 * @typedef HeadingsTimelineFontsUserAppSettings
 * @property {String} font
 * @property {String} fontSize
 */

/**
 * @typedef PlotlinesTimelineFontsUserAppSettings
 * @property {String} font
 * @property {String} fontSize
 */

/**
 * @typedef SceneCardTitlesTimelineFontsUserAppSettings
 * @property {String} font
 * @property {String} fontSize
 */

/**
 * @typedef TimelineFontsUserAppSettings
 * @property {HeadingsTimelineFontsUserAppSettings} headings
 * @property {PlotlinesTimelineFontsUserAppSettings} plotlines
 * @property {SceneCardTitlesTimelineFontsUserAppSettings} sceneCardTitles
 */

/**
 * @typedef FontsUserAppSettings
 * @property {RceFontsUserAppSettings} rce
 * @property {GlobalFontsUserAppSettings} global
 * @property {TimelineFontsUserAppSettings} timeline
 */

/**
 * @typedef UserAppSettings
 * @property {boolean} [enableOfflineModeV2]
 * @property {boolean} autoDownloadUpdate
 * @property {boolean} notifyUpdatesAvailable
 * @property {boolean} autoSave
 * @property {number | Null} backupDays
 * @property {String} backupLocation
 * @property {String} dark
 * @property {String} [themeSource]
 * @property {number | Null} numberOfBackups
 * @property {'never-delete' | 'days' | 'number'} backupType
 * @property {boolean} [localBackups]
 * @property {boolean} openDashboardFirst
 * @property {boolean} streamFriendly
 * @property {boolean} useSpellcheck
 * @property {boolean} useCardColorAsOutline
 * @property {String} font
 * @property {number} fontSize
 * @property {Array<String>} recentColors
 * @property {String | Null} email
 * @property {String | Null} frbId
 * @property {boolean} choseProMode
 * @property {boolean} chosePlottrMode
 * @property {boolean} choseTrialMode
 * @property {FontsUserAppSettings} fonts
 * @property {boolean} [defaultFolder]
 * @property {String} [defaultFolderLocation]
 */

/**
 * @typedef AppSettings
 * @property {boolean} showTheTour
 * @property {boolean} backup
 * @property {boolean} allowPrerelease
 * @property {boolean} forceDevTools
 * @property {boolean} trialMode
 * @property {boolean} canGetUpdates
 * @property {boolean} isInGracePeriod
 * @property {number} gracePeriodEnd
 * @property {boolean} canEdit
 * @property {boolean} canExport
 * @property {Locale | Null} locale
 * @property {boolean} isOnboardingDone
 * @property {UserAppSettings} user
 */

/**
 * TODO: check this type
 * @typedef {Object} UserSettings
 */

/**
 * @typedef Settings
 * @property {ExportSettings | Null} exportSettings
 * @property {AppSettings | Null} appSettings
 * @property {UserSettings | Null} userSettings
 */

/**
 * @typedef SecondsAndNanos
 * @property {number} seconds
 * @property {number} nanoseconds
 */

/**
 * @typedef BackupsFoldersEntry
 * @property {String} name
 * @property {number} size
 * @property {number} lastEdited
 * @property {String} [fileId]
 * @property {String} [storagePath]
 * @property {String} [id]
 * @property {SecondsAndNanos} [lastModified]
 */

/**
 * @typedef BackupFolder
 * @property {String} path
 * @property {String} date
 * @property {Array<BackupsFoldersEntry>} backups
 */

/**
 * @typedef Backups
 * @property {Array<BackupFolder>} folders
 */

/**
 * @typedef SessionApplicationState
 * @property {boolean} loggingIn
 * @property {boolean} checkingSession
 * @property {boolean} sessionChecked
 * @property {boolean | null} loggedIn
 */

/**
 * @typedef ImagePublicURL
 * @property {String} publicUrl
 * @property {number} timestamp
 */

/**
 * @typedef {Record<String, ImagePublicURL>} ImageCache
 */

/**
 * @typedef AudioPublicURL
 * @property {String} publicUrl
 * @property {number} timestamp
 */

/**
 * @typedef {Record<String, AudioPublicURL>} AudioCache
 */

/**
 * @typedef ToastNotificationsSystem
 * @property {String | Null} cardAction
 * @property {'series' | number | Null} newBookId
 * @property {boolean} visible
 * @property {String | Null} lineAction
 */

/**
 * @typedef SystemNotifications
 * @property {String | Null} message
 * @property {ReturnType<typeof setTimeout> | Null} timeout
 * @property {ToastNotificationsSystem} toast
 */

/**
 * @typedef LastClickDomEventsSystem
 * @property {number | Null} x
 * @property {number | Null} y
 * @property {number} counter
 */

/**
 * @typedef Coord
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef DroppedBeatDomEventsSystem
 * @property {Coord | Null} coord
 * @property {number | Null} id
 */

/**
 * @typedef DomEvents
 * @property {LastClickDomEventsSystem} lastClick
 * @property {DroppedBeatDomEventsSystem} droppedBeat
 */

/**
 * @typedef TestingAndDiagnosis
 * @property {boolean} testingAndDiagnosisEnabled
 * @property {boolean} v2
 */

/**
 * @typedef HistoryEntry
 * @property {PlottrFile} state
 * @property {String} actionType
 * @property {String} actionLabel
 * @property {number} count
 * @property {number} batchDepth
 */

/**
 * @typedef Undo
 * @property {Array<HistoryEntry>} history
 * @property {Array<HistoryEntry>} future
 * @property {number} generation
 * @property {boolean} recentlyUndidOrRedid
 * @property {boolean} showUndoRedo
 * @property {number} changeId
 */

/**
 * @typedef OfflineFilesEntry
 * @property {number | null} lastOpened
 * @property {String} fileName
 * @property {String} fileURL
 * @property {FilePermission} permission
 * @property {boolean} isOfflineBackup
 * @property {boolean} isCloudFile
 * @property {number | Null} lastModified
 */

/**
 * @typedef CreatedOfflineFileEntry
 * @property {number} lastOpened
 * @property {String} fileName
 * @property {String} fileURL
 * @property {FilePermission} permission
 */

/**
 * @typedef {Record<FileURL, CreatedOfflineFileEntry>} CreatedOfflineFiles
 */

/**
 * @typedef Offline
 * @property {Array<OfflineFilesEntry>} offlineFiles
 * @property {Array<CreatedOfflineFileEntry>} createdOfflineFiles
 */

/**
 * @typedef ProjectApplicationState
 * @property {boolean} loadingPlottrFileList
 * @property {boolean} loadingProFileList
 * @property {boolean} fileListLoaded
 * @property {boolean} importingNewProject
 * @property {boolean} isImportingAProject
 */

/**
 * @typedef FileApplicationState
 * @property {FileURL | Null} fileURLToBoot
 * @property {Null | String} filePathToUpload
 * @property {boolean} checkingFileToLoad
 * @property {boolean} checkedFileToLoad
 * @property {boolean} creatingCloudFile
 * @property {boolean} uploadingFileToCloud
 * @property {boolean} loadingFile
 * @property {boolean} fileLoaded
 * @property {boolean} renamingFile
 * @property {boolean} deletingFile
 * @property {boolean} savingFileAs
 * @property {Null | String} pathToProjectDuringBoot
 * @property {boolean} noFileToShow
 * @property {boolean} errorLoadingFile
 * @property {boolean} errorIsUpdateError
 */

/**
 * @typedef LicenseApplicationState
 * @property {boolean} loadingTrial
 * @property {boolean} trialLoaded
 * @property {boolean} loadingLicense
 * @property {boolean} licenseLoaded
 * @property {boolean} fetchingLicense
 * @property {boolean} fetchedLicense
 * @property {boolean} checkingProSubscription
 * @property {boolean} proSubscriptionChecked
 * @property {boolean} fetchingProSubscription
 * @property {boolean} fetchedProSubscription
 * @property {boolean | Null} hasActiveProSubscription
 * @property {boolean | Null} needsToCheckProSubscription
 * @property {boolean | Null} hasActivePlottrSubscription
 * @property {boolean | Null} needsToCheckPlottrSubscription
 * @property {boolean | Null} hasActiveTrial
 */

/**
 * @typedef SettingsApplicationState
 * @property {boolean} loadingSettings
 * @property {boolean} settingsLoaded
 * @property {boolean} loadingExportConfig
 * @property {boolean} exportConfigLoaded
 */

/**
 * @typedef ProOnboardingApplicationState
 * @property {boolean} forceLicenseCheck
 * @property {boolean} isOnboarding
 * @property {boolean} isOnboardingFromRoot
 * @property {Null | number} onboardingStep
 */

/**
 * @typedef SettingsWizardApplicationState
 * @property {boolean} isInSettingsWizard
 * @property {Null | number} wizardStep
 * @property {Null | Locale} stagedLanguage
 */

/**
 * @typedef UpdateInfo
 * @property {String} version
 */

/**
 * @typedef UpdateApplicationState
 * @property {boolean} requestedCheck
 * @property {boolean} shouldCheck
 * @property {boolean} checking
 * @property {boolean} available
 * @property {number} percentDownloaded
 * @property {boolean} downloadInProgress
 * @property {Null | String} error
 * @property {UpdateInfo | Null} info
 * @property {boolean} notifierHidden
 */

/**
 * @typedef WorkApplicationState
 * @property {boolean} busy
 */

/**
 * @typedef UserInteractionsApplicationState
 * @property {number} jumpCounter
 * @property {'EDITING'|'VIEWING'|'SEARCHING'} editState
 */

/**
 * @typedef DashboardApplicationState
 * @property {Null | String} openTo
 * @property {boolean} closed
 */

/**
 * @typedef BootingApplicationState
 * @property {boolean} firstTimeBooting
 * @property {boolean} receivedWindowState
 * @property {number} percentageBooted
 * @property {String} descriptionOfBootingPhase
 */

/**
 * @typedef ShutdownApplicationState
 * @property {boolean} isQuitting
 */

/**
 * @typedef {'LOADING'|'PRO_ONBOARDING'|'CHOICE'|'TRIAL_EXPIRED'|'PLEASE_CONNECT_TO_THE_INTERNET'|'PLOTTR_LICENSE_EXPIRED'|'PRO_LICENSE_EXPIRED'|'LOGIN'|'UPlOAD_LAST_OPENED_FILE_TO_PRO'|'ERROR_LOADING_FILE'|'SETTINGS_WIZARD'|'OFFLINE_SPLASH_SCREEN'|'DASHBOARD'|'APP'|'CHECKING_LICENSE'|'PRO_FILE_FROM_PLOTTR'} MainApplicationStateToShow
 */

/**
 * @typedef NetworkApplicationState
 * @property {boolean | Null} isOnline
 */

/**
 * @typedef FeatureSetApplicationState
 * @property {'PRO' | 'PLOTTR' | 'TRIAL' | Null} mode
 * @property {boolean | Null} offlineModeEnabled
 */

/**
 * @typedef ApplicationState
 * @property {ProjectApplicationState} project
 * @property {FileApplicationState} file
 * @property {SessionApplicationState} session
 * @property {LicenseApplicationState} license
 * @property {SettingsApplicationState} settings
 * @property {ProOnboardingApplicationState} proOnboarding
 * @property {SettingsWizardApplicationState} settingsWizard
 * @property {UpdateApplicationState} update
 * @property {WorkApplicationState} work
 * @property {UserInteractionsApplicationState} userInteractions
 * @property {DashboardApplicationState} dashboard
 * @property {BootingApplicationState} booting
 * @property {ShutdownApplicationState} shutdown
 * @property {NetworkApplicationState} network
 * @property {MainApplicationStateToShow} displayState
 * @property {FeatureSetApplicationState} featureSet
 * @property {boolean} isProduction
 */

/**
 * @typedef {Array<Idea>} Ideas
 */

/**
 * @typedef {Array<Thread>} Threads
 */

/**
 * @typedef PocketIdeasState
 * @property {Ideas} ideas
 * @property {Threads} threads
 * @property {UniversalIdeaId | null} ideaToDisplay
 */

/**
 * @typedef SystemState
 * @property {SystemError} error
 * @property {SystemPermission} permission
 * @property {SystemProject} project
 * @property {Client} client
 * @property {SystemLicense} license
 * @property {Account} account
 * @property {Array<KnownFilesEntry>} knownFiles
 * @property {Templates} templates
 * @property {Settings} settings
 * @property {Backups} backups
 * @property {ApplicationState} applicationState
 * @property {ImageCache | Null} imageCache
 * @property {AudioCache | Null} audioCache
 * @property {SystemNotifications} notifications
 * @property {DomEvents} domEvents
 * @property {TestingAndDiagnosis} testingAndDiagnosis
 * @property {Undo} undo
 * @property {Offline} offline
 * @property {PocketIdeasState} ideas
 */

/**
 * @param {unknown} o
 * @return {KnownFilesEntry | Null}
 */
export const validKnownFileOrNull = (o) => {
  if (o && typeof o === 'object') {
    if (
      (('pathToContainingFolder' in o &&
        Array.isArray(o.pathToContainingFolder) &&
        o.pathToContainingFolder.every((s) => typeof s === 'string')) ||
        !('pathToContainingFolder' in o)) &&
      'fileURL' in o &&
      typeof o.fileURL === 'string' &&
      'fileName' in o &&
      typeof o.fileName === 'string' &&
      'lastOpened' in o &&
      (typeof o.lastOpened === 'number' || o.lastOpened === null) &&
      (('isTempFile' in o && typeof o.isTempFile === 'boolean') || !('isTempFile' in o)) &&
      (('path' in o && typeof o.path === 'string') || !('path' in o)) &&
      (('permission' in o && typeof o.permission === 'string') || !('permission' in o)) &&
      (('id' in o && typeof o.id === 'string') || !('id' in o)) &&
      (('isCloudFile' in o && typeof o.isCloudFile === 'boolean') || !('isCloudFile' in o)) &&
      (('isOfflineBackup' in o && typeof o.isOfflineBackup === 'boolean') ||
        !('isOfflineBackup' in o))
    ) {
      return {
        ...('pathToContainingFolder' in o &&
        Array.isArray(o.pathToContainingFolder) &&
        o.pathToContainingFolder.every((s) => typeof s === 'string')
          ? { pathToContainingFolder: o.pathToContainingFolder }
          : {}),
        fileURL: o.fileURL,
        fileName: o.fileName,
        lastOpened: o.lastOpened,
        ...('path' in o && typeof o.path === 'string' ? { path: o.path } : {}),
        ...('id' in o && typeof o.id === 'string' ? { id: o.id } : {}),
        ...('isTempFile' in o && typeof o.isTempFile === 'boolean'
          ? { isTempFile: o.isTempFile }
          : {}),
        ...('isCloudFile' in o && typeof o.isCloudFile === 'boolean'
          ? { isCloudFile: o.isCloudFile }
          : {}),
        ...('isOfflineBackup' in o && typeof o.isOfflineBackup === 'boolean'
          ? { isOfflineBackup: o.isOfflineBackup }
          : {}),
      }
    } else {
      return null
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} knownFiles
 * @return {Array<KnownFilesEntry>}
 */
export const onlyValidKnownFiles = (knownFiles) => {
  if (Array.isArray(knownFiles)) {
    /**
     * @param {Array<KnownFilesEntry>} acc
     * @param {unknown} next
     * @return {Array<KnownFilesEntry>}
     */
    const accumulateValidKnownFiles = (acc, next) => {
      const validKnownFile = validKnownFileOrNull(next)
      if (validKnownFile) {
        return [validKnownFile, ...acc]
      } else {
        return acc
      }
    }
    return knownFiles.reduce(accumulateValidKnownFiles, [])
  } else {
    return []
  }
}

/**
 * @param {unknown} trial
 * @return {TrialInfo | Null}
 */
export const validTrialOrNull = (trial) => {
  if (
    trial &&
    typeof trial === 'object' &&
    'endsAt' in trial &&
    typeof trial.endsAt === 'number' &&
    'extensions' in trial &&
    typeof trial.extensions === 'number' &&
    'startsAt' in trial &&
    typeof trial.startsAt === 'number'
  ) {
    return {
      endsAt: trial.endsAt,
      extensions: trial.extensions,
      startsAt: trial.startsAt,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} customTemplates
 * @return {Array<CustomTemplate>}
 */
export const keepOnlyValidCustomTemplates = (customTemplates) => {
  if (Array.isArray(customTemplates)) {
    /**
     * @param {Array<CustomTemplate>} acc
     * @param {unknown} next
     * @return {Array<CustomTemplate>}
     */
    const accumulateValidTemplates = (acc, next) => {
      const validCustomTemplate = validCustomTemplateOrNull(next)
      if (validCustomTemplate) {
        return [...acc, validCustomTemplate]
      } else {
        return acc
      }
    }
    return customTemplates.reduce(accumulateValidTemplates, [])
  } else {
    return []
  }
}

/**
 * @param {unknown} uncheckedCustomTemplate
 * @return {unknown}
 */
export const fixInvalidBeatsOnPlotlineTemplate = (uncheckedCustomTemplate) => {
  if (
    uncheckedCustomTemplate &&
    typeof uncheckedCustomTemplate === 'object' &&
    'type' in uncheckedCustomTemplate &&
    uncheckedCustomTemplate.type === 'plotlines' &&
    'id' in uncheckedCustomTemplate &&
    typeof uncheckedCustomTemplate.id === 'string'
  ) {
    const templateId = uncheckedCustomTemplate.id
    const templateData =
      'templateData' in uncheckedCustomTemplate &&
      uncheckedCustomTemplate.templateData &&
      typeof uncheckedCustomTemplate.templateData === 'object'
        ? uncheckedCustomTemplate.templateData
        : {}
    const beats =
      'beats' in templateData && typeof templateData.beats === 'object' && templateData.beats
        ? templateData.beats
        : {}
    /**
     * @param {unknown} beat
     * @return {unknown}
     */
    const addMissingAttributesToBeat = (beat) => {
      if (beat && typeof beat === 'object') {
        const newBeat = {
          ...beat,
          ...('expanded' in beat && typeof beat.expanded === 'boolean'
            ? { expanded: beat.expanded }
            : { expanded: true }),
          ...('fromTemplateId' in beat &&
          (typeof beat.fromTemplateId === 'string' || beat.fromTemplateId === null)
            ? { fromTemplateId: beat.fromTemplateId }
            : { fromTemplateId: templateId }),
        }
        if (isEqual(newBeat, beat)) {
          return beat
        } else {
          return newBeat
        }
      } else {
        return beat
      }
    }
    /**
     * @param {unknown} beatTree
     * @return {object}
     */
    const fixBeatsInBeatTree = (beatTree) => {
      if (
        typeof beatTree === 'object' &&
        beatTree &&
        'index' in beatTree &&
        typeof beatTree.index === 'object' &&
        beatTree.index
      ) {
        return {
          ...beatTree,
          index: Object.entries(beatTree.index).reduce((acc, next) => {
            const [beatId, beat] = next
            return {
              ...acc,
              [beatId]: addMissingAttributesToBeat(beat),
            }
          }, {}),
        }
      } else {
        return {
          index: {},
          children: {},
          heap: {},
        }
      }
    }
    const fixedBeats = Object.entries(beats).reduce((acc, next) => {
      const [bookId, nextBeatTree] = next
      return {
        ...acc,
        [bookId]: fixBeatsInBeatTree(nextBeatTree),
      }
    }, {})
    return {
      ...uncheckedCustomTemplate,
      templateData: {
        ...templateData,
        beats: fixedBeats,
      },
    }
  } else {
    return uncheckedCustomTemplate
  }
}

/**
 * @param {unknown} uncheckedCustomTemplate
 * @return {unknown}
 */
export const fixInvalidLinesOnPlotlineTemplate = (uncheckedCustomTemplate) => {
  if (
    uncheckedCustomTemplate &&
    typeof uncheckedCustomTemplate === 'object' &&
    'type' in uncheckedCustomTemplate &&
    uncheckedCustomTemplate.type === 'plotlines'
  ) {
    const templateData =
      'templateData' in uncheckedCustomTemplate &&
      uncheckedCustomTemplate.templateData &&
      typeof uncheckedCustomTemplate.templateData === 'object'
        ? uncheckedCustomTemplate.templateData
        : {}
    const lines =
      'lines' in templateData && Array.isArray(templateData.lines) ? templateData.lines : []
    /**
     * @param {unknown} line
     * @return {unknown}
     */
    const addMissingAttributesToLine = (line) => {
      if (line && typeof line === 'object') {
        const newLine = {
          ...line,
          ...('pinnedPosition' in line && typeof line.pinnedPosition === 'number'
            ? { pinnedPosition: line.pinnedPosition }
            : { pinnedPosition: null }),
          ...('characterId' in line && typeof line.characterId === 'number'
            ? { characterId: line.characterId }
            : { characterId: null }),
          ...('isPinned' in line && typeof line.isPinned === 'number'
            ? { isPinned: line.isPinned }
            : { isPinned: false }),
        }
        if (isEqual(newLine, line)) {
          return line
        } else {
          return newLine
        }
      } else {
        return line
      }
    }
    const fixedLines = lines.map((line) => {
      return addMissingAttributesToLine(line)
    })
    return {
      ...uncheckedCustomTemplate,
      templateData: {
        ...templateData,
        lines: fixedLines,
      },
    }
  } else {
    return uncheckedCustomTemplate
  }
}

/**
 * @param {unknown} uncheckedCustomTemplate
 * @return {unknown}
 */
export const fixInvalidCardsOnPlotlineTemplate = (uncheckedCustomTemplate) => {
  if (
    uncheckedCustomTemplate &&
    typeof uncheckedCustomTemplate === 'object' &&
    'type' in uncheckedCustomTemplate &&
    uncheckedCustomTemplate.type === 'plotlines'
  ) {
    const templateData =
      'templateData' in uncheckedCustomTemplate &&
      uncheckedCustomTemplate.templateData &&
      typeof uncheckedCustomTemplate.templateData === 'object'
        ? uncheckedCustomTemplate.templateData
        : {}
    const cards =
      'cards' in templateData && Array.isArray(templateData.cards) ? templateData.cards : []
    /**
     * @param {unknown} card
     * @return {unknown}
     */
    const addMissingAttributesToCard = (card) => {
      if (card && typeof card === 'object') {
        const newCard = {
          ...card,
          ...('positionInBeat' in card && typeof card.positionInBeat === 'number'
            ? { positionInBeat: card.positionInBeat }
            : { positionInBeat: null }),
          ...('color' in card && typeof card.color === 'string'
            ? { color: card.color }
            : { color: null }),
          ...('notes' in card && Array.isArray(card.notes) ? { notes: card.notes } : { notes: [] }),
          ...('positionWithinLine' in card && typeof card.positionWithinLine === 'number'
            ? { positionWithinLine: card.positionWithinLine }
            : { positionWithinLine: null }),
          ...('audioClipId' in card && typeof card.audioClipId === 'number'
            ? { audioClipId: card.audioClipId }
            : { audioClipId: null }),
        }
        if (isEqual(newCard, card)) {
          return card
        } else {
          return newCard
        }
      } else {
        return card
      }
    }
    const fixedCards = cards.map((card) => {
      return addMissingAttributesToCard(card)
    })
    return {
      ...uncheckedCustomTemplate,
      templateData: {
        ...templateData,
        cards: fixedCards,
      },
    }
  } else {
    return uncheckedCustomTemplate
  }
}

/**
 * @param {unknown} uncheckedCustomTemplate
 * @return {unknown}
 */
export const fixInvalidPlotlineTemplateHierarchy = (uncheckedCustomTemplate) => {
  if (
    uncheckedCustomTemplate &&
    typeof uncheckedCustomTemplate === 'object' &&
    'type' in uncheckedCustomTemplate &&
    uncheckedCustomTemplate.type === 'plotlines'
  ) {
    const templateData =
      'templateData' in uncheckedCustomTemplate &&
      uncheckedCustomTemplate.templateData &&
      typeof uncheckedCustomTemplate.templateData === 'object'
        ? uncheckedCustomTemplate.templateData
        : {}
    const hierarchyLevels =
      'hierarchyLevels' in templateData &&
      typeof templateData.hierarchyLevels === 'object' &&
      templateData.hierarchyLevels
        ? templateData.hierarchyLevels
        : {}
    if (
      '1' in hierarchyLevels &&
      hierarchyLevels['1'] &&
      typeof hierarchyLevels['1'] === 'object' &&
      '0' in hierarchyLevels['1'] &&
      typeof hierarchyLevels['1']['0'] === 'object' &&
      hierarchyLevels['1']['0']
    ) {
      return {
        ...uncheckedCustomTemplate,
        templateData: { ...templateData, hierarchyLevels: hierarchyLevels['1'] },
      }
    } else {
      return uncheckedCustomTemplate
    }
  } else {
    return uncheckedCustomTemplate
  }
}

/**
 * @param {unknown} customTemplate
 * @return {customTemplate}
 */
export const fixInvalidCustomTemplate = (customTemplate) => {
  return fixInvalidCardsOnPlotlineTemplate(
    fixInvalidLinesOnPlotlineTemplate(
      fixInvalidBeatsOnPlotlineTemplate(fixInvalidPlotlineTemplateHierarchy(customTemplate))
    )
  )
}

/**
 * @param {unknown} templates
 * @return {BuiltInTemplateState}
 */
export const keepOnlyValidTemplates = (templates) => {
  if (Array.isArray(templates)) {
    /**
     * @param {BuiltInTemplateState} acc
     * @param {[String, object]} next
     * @return {BuiltInTemplateState}
     */
    const accumulateValidTemplates = (acc, next) => {
      const [key, value] = next
      const builtInTemplate = validBuiltInTemplateOrNull(value)
      if (key && value && builtInTemplate) {
        return {
          ...acc,
          [key]: builtInTemplate,
        }
      } else {
        return acc
      }
    }
    return Object.entries(templates).reduce(accumulateValidTemplates, {})
  } else {
    return {}
  }
}

/**
 * @param {unknown} outlineScrivenerExportSettings
 * @return {OutlineScrivenerExportSettings | Null}
 */
const validOutlineScrivenerExportSettingsOrNull = (outlineScrivenerExportSettings) => {
  if (
    outlineScrivenerExportSettings &&
    typeof outlineScrivenerExportSettings === 'object' &&
    'export' in outlineScrivenerExportSettings &&
    typeof outlineScrivenerExportSettings.export === 'boolean' &&
    'columnOrRow' in outlineScrivenerExportSettings &&
    (outlineScrivenerExportSettings.columnOrRow === 'column' ||
      outlineScrivenerExportSettings.columnOrRow === 'row') &&
    'sceneCards' in outlineScrivenerExportSettings &&
    typeof outlineScrivenerExportSettings.sceneCards === 'boolean' &&
    'plotlineInTitle' in outlineScrivenerExportSettings &&
    typeof outlineScrivenerExportSettings.plotlineInTitle === 'boolean' &&
    'attachments' in outlineScrivenerExportSettings &&
    typeof outlineScrivenerExportSettings.attachments === 'boolean' &&
    'description' in outlineScrivenerExportSettings &&
    typeof outlineScrivenerExportSettings.description === 'boolean' &&
    'descriptionHeading' in outlineScrivenerExportSettings &&
    typeof outlineScrivenerExportSettings.descriptionHeading === 'boolean' &&
    'where' in outlineScrivenerExportSettings &&
    typeof outlineScrivenerExportSettings.where === 'string' &&
    (outlineScrivenerExportSettings.where === 'synopsis' ||
      outlineScrivenerExportSettings.where === 'body' ||
      outlineScrivenerExportSettings.where === 'notes') &&
    'customAttributes' in outlineScrivenerExportSettings &&
    typeof outlineScrivenerExportSettings.customAttributes === 'boolean' &&
    'templates' in outlineScrivenerExportSettings &&
    typeof outlineScrivenerExportSettings.templates === 'boolean' &&
    'filter' in outlineScrivenerExportSettings &&
    (Array.isArray(outlineScrivenerExportSettings.filter) ||
      outlineScrivenerExportSettings.filter === null)
  ) {
    return {
      export: outlineScrivenerExportSettings.export,
      columnOrRow: outlineScrivenerExportSettings.columnOrRow,
      sceneCards: outlineScrivenerExportSettings.sceneCards,
      plotlineInTitle: outlineScrivenerExportSettings.plotlineInTitle,
      attachments: outlineScrivenerExportSettings.attachments,
      description: outlineScrivenerExportSettings.description,
      descriptionHeading: outlineScrivenerExportSettings.descriptionHeading,
      where: outlineScrivenerExportSettings.where,
      customAttributes: outlineScrivenerExportSettings.customAttributes,
      templates: outlineScrivenerExportSettings.templates,
      filter: outlineScrivenerExportSettings.filter,
    }
  } else {
    return null
  }
}

/**
 * @param {GeneralScrivenerExportSettings} generalScrivenerExportSettings
 * @return {GeneralScrivenerExportSettings | Null}
 */
const validGeneralScrivenerExportSettingsOrNull = (generalScrivenerExportSettings) => {
  if (generalScrivenerExportSettings) {
    // NOTE: we don't know much about this type, and it was the empty
    // object in my persisted settings at the time of writing this.
    return generalScrivenerExportSettings
  } else {
    return null
  }
}

/**
 * @param {unknown} charactersScrivenerExportSettings
 * @return {CharactersScrivenerExportSettings | Null}
 */
const validCharactersScrivenerExportSettingsOrNull = (charactersScrivenerExportSettings) => {
  if (
    charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings === 'object' &&
    'export' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.export === 'boolean' &&
    'heading' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.heading === 'boolean' &&
    'description' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.description === 'boolean' &&
    'descriptionHeading' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.descriptionHeading === 'boolean' &&
    'attachments' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.attachments === 'boolean' &&
    'notesHeading' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.notesHeading === 'boolean' &&
    'notes' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.notes === 'boolean' &&
    'customAttributes' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.customAttributes === 'boolean' &&
    'templates' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.templates === 'boolean' &&
    'tags' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.tags === 'boolean' &&
    'categoryHeading' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.categoryHeading === 'boolean' &&
    'category' in charactersScrivenerExportSettings &&
    typeof charactersScrivenerExportSettings.category === 'boolean'
  ) {
    return {
      export: charactersScrivenerExportSettings.export,
      heading: charactersScrivenerExportSettings.heading,
      description: charactersScrivenerExportSettings.description,
      descriptionHeading: charactersScrivenerExportSettings.descriptionHeading,
      attachments: charactersScrivenerExportSettings.attachments,
      notesHeading: charactersScrivenerExportSettings.notesHeading,
      notes: charactersScrivenerExportSettings.notes,
      customAttributes: charactersScrivenerExportSettings.customAttributes,
      templates: charactersScrivenerExportSettings.templates,
      tags: charactersScrivenerExportSettings.tags,
      categoryHeading: charactersScrivenerExportSettings.categoryHeading,
      category: charactersScrivenerExportSettings.category,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} placesScrivenerExportSettings
 * @return {PlacesScrivenerExportSettings | Null}
 */
const validPlacesScrivenerExportSettingsOrNull = (placesScrivenerExportSettings) => {
  if (
    placesScrivenerExportSettings &&
    typeof placesScrivenerExportSettings === 'object' &&
    'export' in placesScrivenerExportSettings &&
    typeof placesScrivenerExportSettings.export === 'boolean' &&
    'heading' in placesScrivenerExportSettings &&
    typeof placesScrivenerExportSettings.heading === 'boolean' &&
    'description' in placesScrivenerExportSettings &&
    typeof placesScrivenerExportSettings.description === 'boolean' &&
    'descriptionHeading' in placesScrivenerExportSettings &&
    typeof placesScrivenerExportSettings.descriptionHeading === 'boolean' &&
    'attachments' in placesScrivenerExportSettings &&
    typeof placesScrivenerExportSettings.attachments === 'boolean' &&
    'notesHeading' in placesScrivenerExportSettings &&
    typeof placesScrivenerExportSettings.notesHeading === 'boolean' &&
    'notes' in placesScrivenerExportSettings &&
    typeof placesScrivenerExportSettings.notes === 'boolean' &&
    'customAttributes' in placesScrivenerExportSettings &&
    typeof placesScrivenerExportSettings.customAttributes === 'boolean' &&
    'tags' in placesScrivenerExportSettings &&
    typeof placesScrivenerExportSettings.tags === 'boolean'
  ) {
    return {
      export: placesScrivenerExportSettings.export,
      heading: placesScrivenerExportSettings.heading,
      description: placesScrivenerExportSettings.description,
      descriptionHeading: placesScrivenerExportSettings.descriptionHeading,
      attachments: placesScrivenerExportSettings.attachments,
      notesHeading: placesScrivenerExportSettings.notesHeading,
      notes: placesScrivenerExportSettings.notes,
      customAttributes: placesScrivenerExportSettings.customAttributes,
      tags: placesScrivenerExportSettings.tags,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} notesScrivenerExportSettings
 * @return {NotesScrivenerExportSettings | Null}
 */
const validNotesScrivenerExportSettingsOrNull = (notesScrivenerExportSettings) => {
  if (
    notesScrivenerExportSettings &&
    typeof notesScrivenerExportSettings === 'object' &&
    'export' in notesScrivenerExportSettings &&
    typeof notesScrivenerExportSettings.export === 'boolean' &&
    'heading' in notesScrivenerExportSettings &&
    typeof notesScrivenerExportSettings.heading === 'boolean' &&
    'attachments' in notesScrivenerExportSettings &&
    typeof notesScrivenerExportSettings.attachments === 'boolean' &&
    'content' in notesScrivenerExportSettings &&
    typeof notesScrivenerExportSettings.content === 'boolean'
  ) {
    return {
      export: notesScrivenerExportSettings.export,
      heading: notesScrivenerExportSettings.heading,
      attachments: notesScrivenerExportSettings.attachments,
      content: notesScrivenerExportSettings.content,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} scrivenerExportSettings
 * @return {ScrivenerExportSettings | Null}
 */
const validScrivenerExportSettingsOrNull = (scrivenerExportSettings) => {
  const general =
    typeof scrivenerExportSettings === 'object' &&
    scrivenerExportSettings &&
    'general' in scrivenerExportSettings
      ? validGeneralScrivenerExportSettingsOrNull(scrivenerExportSettings.general)
      : null
  const outline =
    typeof scrivenerExportSettings === 'object' &&
    scrivenerExportSettings &&
    'outline' in scrivenerExportSettings
      ? validOutlineScrivenerExportSettingsOrNull(scrivenerExportSettings.outline)
      : null
  const characters =
    typeof scrivenerExportSettings === 'object' &&
    scrivenerExportSettings &&
    'characters' in scrivenerExportSettings
      ? validCharactersScrivenerExportSettingsOrNull(scrivenerExportSettings.characters)
      : null
  const places =
    typeof scrivenerExportSettings === 'object' &&
    scrivenerExportSettings &&
    'places' in scrivenerExportSettings
      ? validPlacesScrivenerExportSettingsOrNull(scrivenerExportSettings.places)
      : null
  const notes =
    typeof scrivenerExportSettings === 'object' &&
    scrivenerExportSettings &&
    'notes' in scrivenerExportSettings
      ? validNotesScrivenerExportSettingsOrNull(scrivenerExportSettings.notes)
      : null
  if (
    scrivenerExportSettings &&
    typeof scrivenerExportSettings === 'object' &&
    general &&
    outline &&
    characters &&
    places &&
    notes
  ) {
    return {
      general: general,
      outline: outline,
      characters: characters,
      places: places,
      notes: notes,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} outlineWordExportSettings
 * @return {OutlineWordExportSettings | Null}
 */
const validOutlineWordExportSettingsOrNull = (outlineWordExportSettings) => {
  if (
    outlineWordExportSettings &&
    typeof outlineWordExportSettings === 'object' &&
    'export' in outlineWordExportSettings &&
    typeof outlineWordExportSettings.export === 'boolean' &&
    'columnOrRow' in outlineWordExportSettings &&
    (outlineWordExportSettings.columnOrRow === 'column' ||
      outlineWordExportSettings.columnOrRow === 'row') &&
    'heading' in outlineWordExportSettings &&
    typeof outlineWordExportSettings.heading === 'boolean' &&
    'sceneCards' in outlineWordExportSettings &&
    typeof outlineWordExportSettings.sceneCards === 'boolean' &&
    'plotlineInTitle' in outlineWordExportSettings &&
    typeof outlineWordExportSettings.plotlineInTitle === 'boolean' &&
    'attachments' in outlineWordExportSettings &&
    typeof outlineWordExportSettings.attachments === 'boolean' &&
    'description' in outlineWordExportSettings &&
    typeof outlineWordExportSettings.description === 'boolean' &&
    'customAttributes' in outlineWordExportSettings &&
    typeof outlineWordExportSettings.customAttributes === 'boolean' &&
    'templates' in outlineWordExportSettings &&
    typeof outlineWordExportSettings.templates === 'boolean' &&
    'filter' in outlineWordExportSettings &&
    (Array.isArray(outlineWordExportSettings.filter) || outlineWordExportSettings.filter === null)
  ) {
    return {
      export: outlineWordExportSettings.export,
      columnOrRow: outlineWordExportSettings.columnOrRow,
      heading: outlineWordExportSettings.heading,
      sceneCards: outlineWordExportSettings.sceneCards,
      plotlineInTitle: outlineWordExportSettings.plotlineInTitle,
      attachments: outlineWordExportSettings.attachments,
      description: outlineWordExportSettings.description,
      customAttributes: outlineWordExportSettings.customAttributes,
      templates: outlineWordExportSettings.templates,
      filter: outlineWordExportSettings.filter,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} generalWordExportSettings
 * @return {GeneralWordExportSettings | Null}
 */
const validGeneralWordExportSettingsOrNull = (generalWordExportSettings) => {
  if (
    generalWordExportSettings &&
    typeof generalWordExportSettings === 'object' &&
    'titlePage' in generalWordExportSettings &&
    typeof generalWordExportSettings.titlePage === 'boolean'
  ) {
    return {
      titlePage: generalWordExportSettings.titlePage,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} charactersWordExportSettings
 * @return {CharactersWordExportSettings | Null}
 */
const validCharactersWordExportSettingsOrNull = (charactersWordExportSettings) => {
  if (
    charactersWordExportSettings &&
    typeof charactersWordExportSettings === 'object' &&
    'export' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.export === 'boolean' &&
    'heading' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.heading === 'boolean' &&
    'images' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.images === 'boolean' &&
    'descriptionHeading' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.descriptionHeading === 'boolean' &&
    'description' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.description === 'boolean' &&
    'attachments' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.attachments === 'boolean' &&
    'notesHeading' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.notesHeading === 'boolean' &&
    'notes' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.notes === 'boolean' &&
    'customAttributes' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.customAttributes === 'boolean' &&
    'templates' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.templates === 'boolean' &&
    'categoryHeading' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.categoryHeading === 'boolean' &&
    'category' in charactersWordExportSettings &&
    typeof charactersWordExportSettings.category === 'boolean'
  ) {
    return {
      export: charactersWordExportSettings.export,
      heading: charactersWordExportSettings.heading,
      images: charactersWordExportSettings.images,
      descriptionHeading: charactersWordExportSettings.descriptionHeading,
      description: charactersWordExportSettings.description,
      attachments: charactersWordExportSettings.attachments,
      notesHeading: charactersWordExportSettings.notesHeading,
      notes: charactersWordExportSettings.notes,
      customAttributes: charactersWordExportSettings.customAttributes,
      templates: charactersWordExportSettings.templates,
      categoryHeading: charactersWordExportSettings.categoryHeading,
      category: charactersWordExportSettings.category,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} placesWordExportSettings
 * return {PlacesWordExportSettings | Null}
 */
const validPlacesWordExportSettingsOrNull = (placesWordExportSettings) => {
  if (
    placesWordExportSettings &&
    typeof placesWordExportSettings === 'object' &&
    'export' in placesWordExportSettings &&
    typeof placesWordExportSettings.export === 'boolean' &&
    'heading' in placesWordExportSettings &&
    typeof placesWordExportSettings.heading === 'boolean' &&
    'images' in placesWordExportSettings &&
    typeof placesWordExportSettings.images === 'boolean' &&
    'descriptionHeading' in placesWordExportSettings &&
    typeof placesWordExportSettings.descriptionHeading === 'boolean' &&
    'description' in placesWordExportSettings &&
    typeof placesWordExportSettings.description === 'boolean' &&
    'attachments' in placesWordExportSettings &&
    typeof placesWordExportSettings.attachments === 'boolean' &&
    'notesHeading' in placesWordExportSettings &&
    typeof placesWordExportSettings.notesHeading === 'boolean' &&
    'notes' in placesWordExportSettings &&
    typeof placesWordExportSettings.notes === 'boolean' &&
    'customAttributes' in placesWordExportSettings &&
    typeof placesWordExportSettings.customAttributes === 'boolean' &&
    'category' in placesWordExportSettings &&
    typeof placesWordExportSettings.category === 'boolean'
  ) {
    return {
      export: placesWordExportSettings.export,
      heading: placesWordExportSettings.heading,
      images: placesWordExportSettings.images,
      descriptionHeading: placesWordExportSettings.descriptionHeading,
      description: placesWordExportSettings.description,
      attachments: placesWordExportSettings.attachments,
      notesHeading: placesWordExportSettings.notesHeading,
      notes: placesWordExportSettings.notes,
      customAttributes: placesWordExportSettings.customAttributes,
      category: placesWordExportSettings.category,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} notesWordExportSettings
 * @return {NotesWordExportSettings | Null}
 */
const validNotesWordExportSettingsOrNull = (notesWordExportSettings) => {
  if (
    notesWordExportSettings &&
    typeof notesWordExportSettings === 'object' &&
    'export' in notesWordExportSettings &&
    typeof notesWordExportSettings.export === 'boolean' &&
    'heading' in notesWordExportSettings &&
    typeof notesWordExportSettings.heading === 'boolean' &&
    'images' in notesWordExportSettings &&
    typeof notesWordExportSettings.images === 'boolean' &&
    'attachments' in notesWordExportSettings &&
    typeof notesWordExportSettings.attachments === 'boolean' &&
    'content' in notesWordExportSettings &&
    typeof notesWordExportSettings.content === 'boolean' &&
    'customAttributes' in notesWordExportSettings &&
    typeof notesWordExportSettings.customAttributes === 'boolean' &&
    'category' in notesWordExportSettings &&
    typeof notesWordExportSettings.category === 'boolean'
  ) {
    return {
      export: notesWordExportSettings.export,
      heading: notesWordExportSettings.heading,
      images: notesWordExportSettings.images,
      attachments: notesWordExportSettings.attachments,
      content: notesWordExportSettings.content,
      customAttributes: notesWordExportSettings.customAttributes,
      category: notesWordExportSettings.category,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} wordExportSettings
 * @return {WordExportSettings | Null}
 */
const validWordExportSettingsOrNull = (wordExportSettings) => {
  const general =
    wordExportSettings && typeof wordExportSettings === 'object' && 'general' in wordExportSettings
      ? validGeneralWordExportSettingsOrNull(wordExportSettings.general)
      : null
  const outline =
    wordExportSettings && typeof wordExportSettings === 'object' && 'outline' in wordExportSettings
      ? validOutlineWordExportSettingsOrNull(wordExportSettings.outline)
      : null
  const characters =
    wordExportSettings &&
    typeof wordExportSettings === 'object' &&
    'characters' in wordExportSettings
      ? validCharactersWordExportSettingsOrNull(wordExportSettings.characters)
      : null
  const places =
    wordExportSettings && typeof wordExportSettings === 'object' && 'places' in wordExportSettings
      ? validPlacesWordExportSettingsOrNull(wordExportSettings.places)
      : null
  const notes =
    wordExportSettings && typeof wordExportSettings === 'object' && 'notes' in wordExportSettings
      ? validNotesWordExportSettingsOrNull(wordExportSettings.notes)
      : null

  if (
    wordExportSettings &&
    typeof wordExportSettings === 'object' &&
    general &&
    outline &&
    characters &&
    places &&
    notes
  ) {
    return {
      general,
      outline,
      characters,
      places,
      notes,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} exportSettings
 * @return {ExportSettings}
 */
export const validExportSettingsOrDefault = (exportSettings) => {
  const scrivener =
    exportSettings && typeof exportSettings === 'object' && 'scrivener' in exportSettings
      ? validScrivenerExportSettingsOrNull(exportSettings.scrivener)
      : null
  const word =
    exportSettings && typeof exportSettings === 'object' && 'word' in exportSettings
      ? validWordExportSettingsOrNull(exportSettings.word)
      : null

  if (
    exportSettings &&
    typeof exportSettings === 'object' &&
    'saveSettings' in exportSettings &&
    typeof exportSettings.saveSettings === 'boolean' &&
    'savedType' in exportSettings &&
    (exportSettings.savedType === 'word' || exportSettings.savedType === 'scrivener') &&
    scrivener &&
    word
  ) {
    return {
      saveSettings: exportSettings.saveSettings,
      savedType: exportSettings.savedType,
      scrivener: scrivener,
      word: word,
    }
  } else {
    return export_config
  }
}

/**
 * @param {unknown} rceUserAppFontSettings
 * @return {RceFontsUserAppSettings}
 */
const validRceUserAppFontSettings = (rceUserAppFontSettings) => {
  if (rceUserAppFontSettings && typeof rceUserAppFontSettings === 'object') {
    const rceFonts = desktop().user.fonts.rce
    const defaultFont =
      'defaultFont' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.defaultFont === 'string'
        ? rceUserAppFontSettings.defaultFont
        : rceFonts.defaultFont
    const defaultFontSize =
      'defaultFontSize' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.defaultFontSize === 'string'
        ? rceUserAppFontSettings.defaultFontSize
        : rceFonts.defaultFontSize
    const defaultDarkModeFontColor =
      'defaultDarkModeFontColor' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.defaultDarkModeFontColor === 'string'
        ? rceUserAppFontSettings.defaultDarkModeFontColor
        : rceFonts.defaultDarkModeFontColor
    const defaultFontColor =
      'defaultFontColor' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.defaultFontColor === 'string'
        ? rceUserAppFontSettings.defaultFontColor
        : rceFonts.defaultFontColor
    const titleFont =
      'titleFont' in rceUserAppFontSettings && typeof rceUserAppFontSettings.titleFont === 'string'
        ? rceUserAppFontSettings.titleFont
        : rceFonts.titleFont
    const titleFontSize =
      'titleFontSize' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.titleFontSize === 'string'
        ? rceUserAppFontSettings.titleFontSize
        : rceFonts.titleFontSize
    const titleFontWeight =
      'titleFontWeight' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.titleFontWeight === 'number'
        ? rceUserAppFontSettings.titleFontWeight
        : rceFonts.titleFontWeight
    const titleDarkModeFontColor =
      'titleDarkModeFontColor' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.titleDarkModeFontColor === 'string'
        ? rceUserAppFontSettings.titleDarkModeFontColor
        : rceFonts.titleDarkModeFontColor
    const titleFontColor =
      'titleFontColor' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.titleFontColor === 'string'
        ? rceUserAppFontSettings.titleFontColor
        : rceFonts.titleFontColor
    const subtitleFont =
      'subtitleFont' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.subtitleFont === 'string'
        ? rceUserAppFontSettings.subtitleFont
        : rceFonts.subtitleFont
    const subtitleFontSize =
      'subtitleFontSize' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.subtitleFontSize === 'string'
        ? rceUserAppFontSettings.subtitleFontSize
        : rceFonts.subtitleFontSize
    const subtitleFontWeight =
      'subtitleFontWeight' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.subtitleFontWeight === 'number'
        ? rceUserAppFontSettings.subtitleFontWeight
        : rceFonts.subtitleFontWeight
    const subtitleDarkModeFontColor =
      'subtitleDarkModeFontColor' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.subtitleDarkModeFontColor === 'string'
        ? rceUserAppFontSettings.subtitleDarkModeFontColor
        : rceFonts.subtitleDarkModeFontColor
    const subtitleFontColor =
      'subtitleFontColor' in rceUserAppFontSettings &&
      typeof rceUserAppFontSettings.subtitleFontColor === 'string'
        ? rceUserAppFontSettings.subtitleFontColor
        : rceFonts.subtitleFontColor

    return {
      defaultFont,
      defaultFontSize,
      defaultDarkModeFontColor,
      defaultFontColor,
      titleFont,
      titleFontSize,
      titleFontWeight,
      titleDarkModeFontColor,
      titleFontColor,
      subtitleFont,
      subtitleFontSize,
      subtitleFontWeight,
      subtitleDarkModeFontColor,
      subtitleFontColor,
    }
  } else {
    return desktop().user.fonts.rce
  }
}

/**
 * @param {unknown} globalFontsUserAppSettings
 * @return {GlobalFontsUserAppSettings}
 */
const validGlobalUserAppFontSettings = (globalFontsUserAppSettings) => {
  if (globalFontsUserAppSettings && typeof globalFontsUserAppSettings === 'object') {
    const globalFonts = desktop().user.fonts.global
    const headingFont =
      'headingFont' in globalFontsUserAppSettings &&
      typeof globalFontsUserAppSettings.headingFont === 'string'
        ? globalFontsUserAppSettings.headingFont
        : globalFonts.headingFont
    const bodyFont =
      'bodyFont' in globalFontsUserAppSettings &&
      typeof globalFontsUserAppSettings.bodyFont === 'string'
        ? globalFontsUserAppSettings.bodyFont
        : globalFonts.bodyFont
    return {
      headingFont,
      bodyFont,
    }
  } else {
    return desktop().user.fonts.global
  }
}

/**
 * @param {unknown} timelineFontAndSize
 * @return {HeadingsTimelineFontsUserAppSettings}
 */
const validTimelineHeadingFontAndSize = (timelineFontAndSize) => {
  if (timelineFontAndSize && typeof timelineFontAndSize === 'object') {
    const headingFont = desktop().user.fonts.timeline.headings
    const font =
      'font' in timelineFontAndSize && typeof timelineFontAndSize.font === 'string'
        ? timelineFontAndSize.font
        : headingFont.font
    const fontSize =
      'fontSize' in timelineFontAndSize && typeof timelineFontAndSize.fontSize === 'string'
        ? timelineFontAndSize.fontSize
        : headingFont.fontSize
    return {
      font,
      fontSize,
    }
  } else {
    return desktop().user.fonts.timeline.headings
  }
}

/**
 * @param {unknown} timelineFontAndSize
 * @return {PlotlinesTimelineFontsUserAppSettings}
 */
const validTimelinePlotlineFontAndSize = (timelineFontAndSize) => {
  if (timelineFontAndSize && typeof timelineFontAndSize === 'object') {
    const plotlineFont = desktop().user.fonts.timeline.plotlines
    const font =
      'font' in timelineFontAndSize && typeof timelineFontAndSize.font === 'string'
        ? timelineFontAndSize.font
        : plotlineFont.font
    const fontSize =
      'fontSize' in timelineFontAndSize && typeof timelineFontAndSize.fontSize === 'string'
        ? timelineFontAndSize.fontSize
        : plotlineFont.fontSize
    return {
      font,
      fontSize,
    }
  } else {
    return desktop().user.fonts.timeline.plotlines
  }
}

/**
 * @param {unknown} timelineFontAndSize
 * @return {PlotlinesTimelineFontsUserAppSettings}
 */
const validTimelineSceneCardFontAndSize = (timelineFontAndSize) => {
  if (timelineFontAndSize && typeof timelineFontAndSize === 'object') {
    const sceneCardFont = desktop().user.fonts.timeline.sceneCardTitles
    const font =
      'font' in timelineFontAndSize && typeof timelineFontAndSize.font === 'string'
        ? timelineFontAndSize.font
        : sceneCardFont.font
    const fontSize =
      'fontSize' in timelineFontAndSize && typeof timelineFontAndSize.fontSize === 'string'
        ? timelineFontAndSize.fontSize
        : sceneCardFont.fontSize
    return {
      font,
      fontSize,
    }
  } else {
    return desktop().user.fonts.timeline.sceneCardTitles
  }
}

/**
 * @param {unknown} timelineFontsUserAppSettings
 * @return {TimelineFontsUserAppSettings}
 */
const validTimelineUserAppFontSettings = (timelineFontsUserAppSettings) => {
  if (timelineFontsUserAppSettings && typeof timelineFontsUserAppSettings === 'object') {
    const defaults = desktop().user.fonts.timeline
    const headings =
      'headings' in timelineFontsUserAppSettings
        ? validTimelineHeadingFontAndSize(timelineFontsUserAppSettings.headings)
        : defaults.headings
    const plotlines =
      'plotlines' in timelineFontsUserAppSettings
        ? validTimelinePlotlineFontAndSize(timelineFontsUserAppSettings.plotlines)
        : defaults.plotlines
    const sceneCardTitles =
      'sceneCardTitles' in timelineFontsUserAppSettings
        ? validTimelineSceneCardFontAndSize(timelineFontsUserAppSettings.sceneCardTitles)
        : defaults.sceneCardTitles
    return {
      headings,
      plotlines,
      sceneCardTitles,
    }
  } else {
    return desktop().user.fonts.timeline
  }
}

/**
 * @param {unknown} userAppFontSettings
 * @return {FontsUserAppSettings}
 */
const validUserAppFontSettings = (userAppFontSettings) => {
  if (userAppFontSettings && typeof userAppFontSettings === 'object') {
    const defaults = desktop().user.fonts
    const rce =
      'rce' in userAppFontSettings
        ? validRceUserAppFontSettings(userAppFontSettings.rce)
        : defaults.rce
    const global =
      'global' in userAppFontSettings
        ? validGlobalUserAppFontSettings(userAppFontSettings.global)
        : defaults.global
    const timeline =
      'timeline' in userAppFontSettings
        ? validTimelineUserAppFontSettings(userAppFontSettings.timeline)
        : defaults.timeline
    return {
      rce,
      global,
      timeline,
    }
  } else {
    return desktop().user.fonts
  }
}

/**
 * @param {unknown} userAppSettings
 * @return {UserAppSettings}
 */
const validUserAppSettings = (userAppSettings) => {
  if (userAppSettings && typeof userAppSettings === 'object') {
    const defaults = desktop().user
    const enableOfflineModeV2 =
      'enableOfflineModeV2' in userAppSettings &&
      typeof userAppSettings.enableOfflineModeV2 === 'boolean'
        ? userAppSettings.enableOfflineModeV2
        : defaults.enableOfflineModeV2
    const autoDownloadUpdate =
      'autoDownloadUpdate' in userAppSettings &&
      typeof userAppSettings.autoDownloadUpdate === 'boolean'
        ? userAppSettings.autoDownloadUpdate
        : defaults.autoDownloadUpdate
    const notifyUpdatesAvailable =
      'notifyUpdatesAvailable' in userAppSettings &&
      typeof userAppSettings.notifyUpdatesAvailable === 'boolean'
        ? userAppSettings.notifyUpdatesAvailable
        : defaults.notifyUpdatesAvailable
    const autoSave =
      'autoSave' in userAppSettings && typeof userAppSettings.autoSave === 'boolean'
        ? userAppSettings.autoSave
        : defaults.autoSave
    const backupDays =
      'backupDays' in userAppSettings &&
      (typeof userAppSettings.backupDays === 'number' || userAppSettings.backupDays === null)
        ? userAppSettings.backupDays
        : defaults.backupDays
    const backupLocation =
      'backupLocation' in userAppSettings && typeof userAppSettings.backupLocation === 'string'
        ? userAppSettings.backupLocation
        : defaults.backupLocation
    const dark =
      'dark' in userAppSettings && typeof userAppSettings.dark === 'string'
        ? userAppSettings.dark
        : defaults.dark
    const themeSource =
      'themeSource' in userAppSettings && typeof userAppSettings.themeSource === 'string'
        ? userAppSettings.themeSource
        : defaults.themeSource
    const numberOfBackups =
      'numberOfBackups' in userAppSettings &&
      (typeof userAppSettings.numberOfBackups === 'number' ||
        userAppSettings.numberOfBackups === null)
        ? userAppSettings.numberOfBackups
        : defaults.numberOfBackups
    const backupType =
      'backupType' in userAppSettings &&
      (userAppSettings.backupType === 'never-delete' ||
        userAppSettings.backupType === 'days' ||
        userAppSettings.backupType === 'number')
        ? userAppSettings.backupType
        : defaults.backupType
    const localBackups =
      'localBackups' in userAppSettings && typeof userAppSettings.localBackups === 'boolean'
        ? userAppSettings.localBackups
        : defaults.localBackups
    const openDashboardFirst =
      'openDashboardFirst' in userAppSettings &&
      typeof userAppSettings.openDashboardFirst === 'boolean'
        ? userAppSettings.openDashboardFirst
        : defaults.openDashboardFirst
    const streamFriendly =
      'streamFriendly' in userAppSettings && typeof userAppSettings.streamFriendly === 'boolean'
        ? userAppSettings.streamFriendly
        : defaults.streamFriendly
    const useSpellcheck =
      'useSpellcheck' in userAppSettings && typeof userAppSettings.useSpellcheck === 'boolean'
        ? userAppSettings.useSpellcheck
        : defaults.useSpellcheck
    const useCardColorAsOutline =
      'useCardColorAsOutline' in userAppSettings &&
      typeof userAppSettings.useCardColorAsOutline === 'boolean'
        ? userAppSettings.useCardColorAsOutline
        : defaults.useCardColorAsOutline
    const font =
      'font' in userAppSettings && typeof userAppSettings.font === 'string'
        ? userAppSettings.font
        : defaults.font
    const fontSize =
      'fontSize' in userAppSettings && typeof userAppSettings.fontSize === 'number'
        ? userAppSettings.fontSize
        : defaults.fontSize
    const recentColors =
      'recentColors' in userAppSettings &&
      Array.isArray(userAppSettings.recentColors) &&
      userAppSettings.recentColors.every((x) => typeof x === 'string')
        ? userAppSettings.recentColors
        : defaults.recentColors
    const email =
      'email' in userAppSettings &&
      (typeof userAppSettings.email === 'string' || userAppSettings.email === null)
        ? userAppSettings.email
        : defaults.email
    const frbId =
      'frbId' in userAppSettings &&
      (typeof userAppSettings.frbId === 'string' || userAppSettings.frbId === null)
        ? userAppSettings.frbId
        : defaults.frbId
    const choseProMode =
      'choseProMode' in userAppSettings && typeof userAppSettings.choseProMode === 'boolean'
        ? userAppSettings.choseProMode
        : defaults.choseProMode
    const chosePlottrMode =
      'chosePlottrMode' in userAppSettings && typeof userAppSettings.chosePlottrMode === 'boolean'
        ? userAppSettings.chosePlottrMode
        : defaults.chosePlottrMode
    const choseTrialMode =
      'choseTrialMode' in userAppSettings && typeof userAppSettings.choseTrialMode === 'boolean'
        ? userAppSettings.choseTrialMode
        : defaults.choseTrialMode
    const defaultFolder =
      'defaultFolder' in userAppSettings && typeof userAppSettings.defaultFolder === 'boolean'
        ? userAppSettings.defaultFolder
        : defaults.defaultFolder
    const defaultFolderLocation =
      'defaultFolderLocation' in userAppSettings &&
      typeof userAppSettings.defaultFolderLocation === 'string'
        ? userAppSettings.defaultFolderLocation
        : defaults.defaultFolderLocation
    const fonts =
      'fonts' in userAppSettings ? validUserAppFontSettings(userAppSettings.fonts) : defaults.fonts

    return {
      fonts,
      enableOfflineModeV2,
      autoDownloadUpdate,
      notifyUpdatesAvailable,
      autoSave,
      backupDays,
      backupLocation,
      dark,
      themeSource,
      numberOfBackups,
      backupType,
      localBackups,
      openDashboardFirst,
      streamFriendly,
      useSpellcheck,
      useCardColorAsOutline,
      font,
      fontSize,
      recentColors,
      email,
      frbId,
      choseProMode,
      chosePlottrMode,
      choseTrialMode,
      defaultFolder,
      defaultFolderLocation,
    }
  } else {
    return desktop().user
  }
}

/**
 * @param {unknown} appSettings
 * @return {AppSettings}
 */
export const validAppSettings = (appSettings) => {
  if (appSettings && typeof appSettings === 'object') {
    const user = ('user' in appSettings && validUserAppSettings(appSettings.user)) || desktop().user
    const defaults = desktop()
    const showTheTour =
      'showTheTour' in appSettings && typeof appSettings.showTheTour === 'boolean'
        ? appSettings.showTheTour
        : defaults.showTheTour
    const backup =
      'backup' in appSettings && typeof appSettings.backup === 'boolean'
        ? appSettings.backup
        : defaults.backup
    const allowPrerelease =
      'allowPrerelease' in appSettings && typeof appSettings.allowPrerelease === 'boolean'
        ? appSettings.allowPrerelease
        : defaults.allowPrerelease
    const forceDevTools =
      'forceDevTools' in appSettings && typeof appSettings.forceDevTools === 'boolean'
        ? appSettings.forceDevTools
        : defaults.forceDevTools
    const trialMode =
      'trialMode' in appSettings && typeof appSettings.trialMode === 'boolean'
        ? appSettings.trialMode
        : defaults.trialMode
    const canGetUpdates =
      'canGetUpdates' in appSettings && typeof appSettings.canGetUpdates === 'boolean'
        ? appSettings.canGetUpdates
        : defaults.canGetUpdates
    const isInGracePeriod =
      'isInGracePeriod' in appSettings && typeof appSettings.isInGracePeriod === 'boolean'
        ? appSettings.isInGracePeriod
        : defaults.isInGracePeriod
    const gracePeriodEnd =
      'gracePeriodEnd' in appSettings && typeof appSettings.gracePeriodEnd === 'number'
        ? appSettings.gracePeriodEnd
        : defaults.gracePeriodEnd
    const canEdit =
      'canEdit' in appSettings && typeof appSettings.canEdit === 'boolean'
        ? appSettings.canEdit
        : defaults.canEdit
    const canExport =
      'canExport' in appSettings && typeof appSettings.canExport === 'boolean'
        ? appSettings.canExport
        : defaults.canExport
    const locale =
      'locale' in appSettings && typeof appSettings.locale === 'string'
        ? appSettings.locale
        : defaults.locale
    const isOnboardingDone =
      ('isOnboardingDone' in appSettings && typeof appSettings.isOnboardingDone === 'boolean') ||
      defaults.isOnboardingDone

    return {
      showTheTour,
      backup,
      allowPrerelease,
      forceDevTools,
      trialMode,
      canGetUpdates,
      isInGracePeriod,
      gracePeriodEnd,
      canEdit,
      canExport,
      // FIXME(ed): enumerate the supported locales and type check
      // them.
      //
      // @ts-ignore
      locale,
      isOnboardingDone,
      user,
    }
  } else {
    return desktop()
  }
}

/**
 * @param {unknown} backupsFoldersEntry
 * @return {BackupsFoldersEntry | Null}
 */
const validBackupsFoldersEntryOrNull = (backupsFoldersEntry) => {
  if (
    backupsFoldersEntry &&
    typeof backupsFoldersEntry === 'object' &&
    'name' in backupsFoldersEntry &&
    typeof backupsFoldersEntry.name === 'string' &&
    'size' in backupsFoldersEntry &&
    typeof backupsFoldersEntry.size === 'number' &&
    'lastEdited' in backupsFoldersEntry &&
    typeof backupsFoldersEntry.lastEdited === 'number'
  ) {
    return {
      name: backupsFoldersEntry.name,
      size: backupsFoldersEntry.size,
      lastEdited: backupsFoldersEntry.lastEdited,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} folder
 * @return {BackupFolder | Null}
 */
const validBackupFolderOrNull = (folder) => {
  if (
    folder &&
    typeof folder === 'object' &&
    'path' in folder &&
    typeof folder.path === 'string' &&
    'date' in folder &&
    typeof folder.date === 'string' &&
    'backups' in folder &&
    Array.isArray(folder.backups) &&
    folder.backups.every(validBackupsFoldersEntryOrNull)
  ) {
    return {
      path: folder.path,
      date: folder.date,
      backups: folder.backups,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} backups
 * @return {Array<BackupFolder> | Null}
 */
export const keepOnlyValidBackups = (backups) => {
  if (backups && Array.isArray(backups)) {
    return backups.reduce((acc, next) => {
      const backupFolder = validBackupFolderOrNull(next)
      if (backupFolder) {
        return [...acc, backupFolder]
      } else {
        return acc
      }
    }, [])
  } else {
    return null
  }
}

/**
 * @param {unknown} imagePublicURL
 * @return {ImagePublicURL | Null}
 */
const validImagePublicURLOrNull = (imagePublicURL) => {
  if (
    imagePublicURL &&
    typeof imagePublicURL === 'object' &&
    'publicUrl' in imagePublicURL &&
    typeof imagePublicURL.publicUrl === 'string' &&
    'timestamp' in imagePublicURL &&
    typeof imagePublicURL.timestamp === 'number'
  ) {
    return {
      publicUrl: imagePublicURL.publicUrl,
      timestamp: imagePublicURL.timestamp,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} imageCache
 * @return {Record<String, ImagePublicURL> | Null}
 */
export const keepOnlyValidImageCacheEntries = (imageCache) => {
  if (imageCache) {
    return Object.entries(imageCache).reduce((acc, next) => {
      const [key, value] = next
      const imagePublicURL = validImagePublicURLOrNull(value)
      if (imagePublicURL) {
        return {
          ...acc,
          [key]: imagePublicURL,
        }
      } else {
        return acc
      }
    }, {})
  } else {
    return null
  }
}

/**
 * @param {unknown} machineInfo
 * @return {MachineInfo | Null}
 */
const validLicenseInfoOrNull = (machineInfo) => {
  if (
    machineInfo &&
    typeof machineInfo === 'object' &&
    'id' in machineInfo &&
    typeof machineInfo.id === 'string' &&
    'os' in machineInfo &&
    typeof machineInfo.os === 'string' &&
    'name' in machineInfo &&
    typeof machineInfo.name === 'string' &&
    'localUserName' in machineInfo &&
    typeof machineInfo.localUserName === 'string'
  ) {
    return {
      id: machineInfo.id,
      os: machineInfo.os,
      name: machineInfo.name,
      localUserName: machineInfo.localUserName,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} license
 * @return {PlottrLicense | ProLicense | Null}
 */
export const validLicenseOrNull = (license) => {
  const machineInfo =
    license && typeof license === 'object' && 'machineInfo' in license
      ? validLicenseInfoOrNull(license.machineInfo)
      : null
  if (
    license &&
    typeof license === 'object' &&
    'secret' in license &&
    typeof license.secret === 'string' &&
    machineInfo &&
    'dateChecked' in license &&
    typeof license.dateChecked === 'string' &&
    'expiresAt' in license &&
    (typeof license.expiresAt === 'string' || license.expiresAt === null)
  ) {
    return {
      secret: license.secret,
      machineInfo,
      dateChecked: license.dateChecked,
      expiresAt: license.expiresAt,
    }
  } else {
    return null
  }
}

/**
 * @param {String} s
 * @return {number | Null}
 */
const parseIntOrNull = (s) => {
  try {
    return parseInt(s)
  } catch (_e) {
    return null
  }
}

/**
 * @param {unknown} offlineFilesEntry
 * @return {OfflineFilesEntry | Null}
 */
const validOfflineFilesEntryOrNull = (offlineFilesEntry) => {
  if (
    offlineFilesEntry &&
    typeof offlineFilesEntry === 'object' &&
    'lastOpened' in offlineFilesEntry &&
    typeof offlineFilesEntry.lastOpened === 'number' &&
    'fileName' in offlineFilesEntry &&
    typeof offlineFilesEntry.fileName === 'string' &&
    'fileURL' in offlineFilesEntry &&
    typeof offlineFilesEntry.fileURL === 'string' &&
    'permission' in offlineFilesEntry &&
    (offlineFilesEntry.permission === 'owner' ||
      offlineFilesEntry.permission === 'collaborator' ||
      offlineFilesEntry.permission === 'viewer') &&
    'isOfflineBackup' in offlineFilesEntry &&
    typeof offlineFilesEntry.isOfflineBackup === 'boolean' &&
    'isCloudFile' in offlineFilesEntry &&
    typeof offlineFilesEntry.isCloudFile === 'boolean' &&
    'lastModified' in offlineFilesEntry &&
    (typeof offlineFilesEntry.lastModified === 'number' ||
      // FIXME(ed): in prior versions of Plottr, we mistakenly saved
      // this timestamp as a string.  We'll convert it here to keep
      // the core sane, but at some point in the future we want to
      // remove this special case.
      typeof offlineFilesEntry.lastModified === 'string')
  ) {
    return {
      lastOpened: offlineFilesEntry.lastOpened,
      fileName: offlineFilesEntry.fileName,
      fileURL: offlineFilesEntry.fileURL,
      permission: offlineFilesEntry.permission,
      isOfflineBackup: offlineFilesEntry.isOfflineBackup,
      isCloudFile: offlineFilesEntry.isCloudFile,
      lastModified:
        typeof offlineFilesEntry.lastModified === 'string'
          ? (parseIntOrNull(offlineFilesEntry.lastModified) ?? new Date().getTime())
          : offlineFilesEntry.lastModified,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} offlineFiles
 * @return {Record<FileURL, OfflineFilesEntry> | Null}
 */
export const keepOnlyValidOfflineFiles = (offlineFiles) => {
  if (offlineFiles) {
    return Object.entries(offlineFiles).reduce((acc, next) => {
      const [key, value] = next
      const offlineFilesEntry = validOfflineFilesEntryOrNull(value)
      if (offlineFilesEntry) {
        return {
          ...acc,
          [key]: offlineFilesEntry,
        }
      } else {
        return acc
      }
    }, {})
  } else {
    return {}
  }
}

/**
 * @param {unknown} o
 * @return {CreatedOfflineFileEntry | Null}
 */
export const validCreatedOfflineFilesEntryOrNull = (o) => {
  if (
    o &&
    typeof o === 'object' &&
    'lastOpened' in o &&
    typeof o.lastOpened === 'number' &&
    'fileName' in o &&
    typeof o.fileName === 'string' &&
    'fileURL' in o &&
    typeof o.fileURL === 'string' &&
    'permission' in o &&
    (o.permission === 'owner' || o.permission === 'collaborator' || o.permission === 'viewer')
  ) {
    return {
      lastOpened: o.lastOpened,
      fileName: o.fileName,
      fileURL: o.fileURL,
      permission: o.permission,
    }
  } else {
    return null
  }
}

/**
 * @param {unknown} createdOfflineFiles
 * @return {CreatedOfflineFiles | Null}
 */
export const keepOnlyValidCreatedOfflineFiles = (createdOfflineFiles) => {
  if (createdOfflineFiles && typeof createdOfflineFiles === 'object') {
    return Object.entries(createdOfflineFiles).reduce((acc, next) => {
      const [key, value] = next
      const offlineFilesEntry = validCreatedOfflineFilesEntryOrNull(value)
      if (offlineFilesEntry) {
        return {
          ...acc,
          [key]: offlineFilesEntry,
        }
      } else {
        return acc
      }
    }, {})
  } else {
    return {}
  }
}
